import React, { useState } from 'react';
import { JobApplication, ApplicationStatus } from '../../types';
import { useApplicationStore } from '../../stores/applicationStore';
import { Timestamp } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';
import { ref, deleteObject } from 'firebase/storage';
import { storage } from '../../services/firebase.config';
import { useAuthStore } from '../../stores/authStore';
import { UpdateApplicationData } from '../../services/applicationService';
import { ArrowLeft, Building2, Calendar, MapPin, Phone, Globe, FileText, Clock, BadgeCheck, CheckCircle, XCircle, AlertCircle, Edit, Save, X, Plus, Trash2, Paperclip, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface Props {
  application: JobApplication;
  onClose: () => void;
}

const statusOptions: ApplicationStatus[] = [
  'In attesa',
  'Colloquio programmato',
  'Accettato',
  'Rifiutato',
  'Scaduto',
];

const statusBadge = {
  'In attesa': { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', icon: <Clock className="w-4 h-4" /> },
  'Colloquio programmato': { color: 'bg-blue-100 text-blue-800 border-blue-200', icon: <BadgeCheck className="w-4 h-4" /> },
  'Accettato': { color: 'bg-green-100 text-green-800 border-green-200', icon: <CheckCircle className="w-4 h-4" /> },
  'Rifiutato': { color: 'bg-red-100 text-red-800 border-red-200', icon: <XCircle className="w-4 h-4" /> },
  'Scaduto': { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: <AlertCircle className="w-4 h-4" /> },
};

export const ApplicationDetail: React.FC<Props> = ({ application, onClose }) => {
  const { updateApplicationInFirestore, updateApplicationStatus } = useApplicationStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [editMode, setEditMode] = useState(false);
  const [position, setPosition] = useState(application.position);
  const [notes, setNotes] = useState(application.notes);
  const [status, setStatus] = useState<ApplicationStatus>(application.status);
  const [attachments, setAttachments] = useState(application.attachments);
  const [showStatusForm, setShowStatusForm] = useState(false);
  const [newStatus, setNewStatus] = useState<ApplicationStatus>(application.status);
  const [statusNote, setStatusNote] = useState('');
  const [showInterviewForm, setShowInterviewForm] = useState(false);
  const [interviewDate, setInterviewDate] = useState('');
  const [interviewTime, setInterviewTime] = useState('');
  const [interviewType, setInterviewType] = useState<'Presenza' | 'Online' | 'Telefono'>('Online');
  const [interviewLocation, setInterviewLocation] = useState('');
  const [interviewContact, setInterviewContact] = useState('');
  const [interviewNotes, setInterviewNotes] = useState('');
  const [interviewReminder, setInterviewReminder] = useState(true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!user?.uid) return;
    
    setSaving(true);
    try {
      const updateData: UpdateApplicationData = {
        position,
        notes,
        status,
        attachments
      };

      await updateApplicationInFirestore(user.uid, application.id, updateData);
      setEditMode(false);
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusUpdate = async () => {
    if (!user?.uid) return;
    
    setSaving(true);
    try {
      await updateApplicationStatus(user.uid, application.id, newStatus, statusNote);
      setStatus(newStatus);
      setShowStatusForm(false);
      setStatusNote('');
    } catch (error) {
      console.error('Errore nell\'aggiornamento stato:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleAddInterview = async () => {
    if (!user?.uid) return;
    
    setSaving(true);
    try {
      const interviewDateTime = new Date(`${interviewDate}T${interviewTime}`);
      
      const updateData: UpdateApplicationData = {
        interviews: [
          ...application.interviews,
          {
            id: uuidv4(),
            date: Timestamp.fromDate(interviewDateTime),
            type: interviewType,
            location: interviewLocation,
            notes: interviewNotes,
            reminder: interviewReminder
          }
        ]
      };

      await updateApplicationInFirestore(user.uid, application.id, updateData);
      setShowInterviewForm(false);
      setInterviewDate('');
      setInterviewTime('');
      setInterviewLocation('');
      setInterviewNotes('');
    } catch (error) {
      console.error('Errore nell\'aggiunta colloquio:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAttachment = async (attachmentIndex: number) => {
    if (!user?.uid) return;
    
    try {
      const attachment = attachments[attachmentIndex];
      if (attachment.url) {
        const storageRef = ref(storage, attachment.url);
        await deleteObject(storageRef);
      }
      
      const newAttachments = attachments.filter((_, index) => index !== attachmentIndex);
      setAttachments(newAttachments);
      
      const updateData: UpdateApplicationData = { attachments: newAttachments };
      await updateApplicationInFirestore(user.uid, application.id, updateData);
    } catch (error) {
      console.error('Errore nell\'eliminazione allegato:', error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 lg:pr-8 space-y-2 lg:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 lg:gap-4">
            <button
              onClick={onClose}
              className="p-1.5 lg:p-2 bg-gray-100 rounded-lg lg:rounded-xl hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-lg lg:text-2xl xl:text-3xl font-bold text-gray-900">
                {editMode ? 'Modifica Candidatura' : application.position}
              </h1>
              <p className="text-sm lg:text-base text-gray-600 mt-0.5 lg:mt-1">{application.companyName}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 lg:gap-3">
            {!editMode && (
              <button
                onClick={() => setEditMode(true)}
                className="flex items-center gap-1.5 lg:gap-2 px-3 lg:px-4 xl:px-6 py-1.5 lg:py-2 xl:py-3 bg-blue-600 text-white rounded-lg lg:rounded-xl shadow-sm hover:bg-blue-700 transition-colors font-medium text-sm lg:text-base"
              >
                <Edit className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                <span>Modifica</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="space-y-2 lg:space-y-6">
        {/* Layout responsive */}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-2 lg:gap-6">
          
          {/* Sezione 1: Informazioni Principali */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6 h-full">
              <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-4">
                <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                  <FileText className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                </div>
                <h3 className="text-base lg:text-xl font-semibold text-gray-900">Informazioni Principali</h3>
              </div>
              
              {editMode ? (
                <div className="space-y-3 lg:space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Posizione
                    </label>
                    <input
                      type="text"
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                      className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Note
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={3}
                      className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5 lg:mb-2">
                      Stato
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value as ApplicationStatus)}
                      className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                  </div>
                  
                  <div className="flex gap-2 lg:gap-3 pt-3 lg:pt-4">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="flex-1 px-3 lg:px-4 py-2 lg:py-3 bg-green-600 text-white rounded-lg lg:rounded-xl hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5 lg:gap-2 text-sm lg:text-base"
                    >
                      <Save className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                      {saving ? 'Salvando...' : 'Salva'}
                    </button>
                    <button
                      onClick={() => setEditMode(false)}
                      className="flex-1 px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-50 transition-colors text-sm lg:text-base"
                    >
                      Annulla
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 lg:space-y-4">
                  <div>
                    <span className="text-sm font-medium text-gray-500">Posizione</span>
                    <p className="text-base lg:text-lg font-semibold text-gray-900">{application.position}</p>
                  </div>
                  
                  <div>
                    <span className="text-sm font-medium text-gray-500">Stato</span>
                    <div className="mt-1 lg:mt-2">
                      <span className={`inline-flex items-center gap-1.5 lg:gap-2 px-2 lg:px-3 py-1.5 lg:py-2 rounded-full text-xs lg:text-sm font-medium border ${statusBadge[application.status].color}`}>
                        {statusBadge[application.status].icon}
                        {application.status}
                      </span>
                    </div>
                  </div>
                  
                  <div>
                    <span className="text-sm font-medium text-gray-500">Data Candidatura</span>
                    <p className="text-base lg:text-lg font-semibold text-gray-900">
                      {application.appliedDate.toDate().toLocaleDateString('it-IT')}
                    </p>
                  </div>
                  
                  <div>
                    <span className="text-sm font-medium text-gray-500">Metodo di Contatto</span>
                    <p className="text-base lg:text-lg font-semibold text-gray-900 capitalize">{application.contactMethod}</p>
                  </div>
                  
                  {application.notes && (
                    <div>
                      <span className="text-sm font-medium text-gray-500">Note</span>
                      <p className="text-gray-900 mt-1 lg:mt-2 bg-gray-50 p-2 lg:p-3 rounded-lg lg:rounded-xl border text-sm lg:text-base">
                        {application.notes}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Sezione 2: Dettagli Azienda */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6 h-full">
              <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-4">
                <div className="p-1.5 lg:p-2 bg-green-100 rounded-lg lg:rounded-xl">
                  <Building2 className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
                </div>
                <h3 className="text-base lg:text-xl font-semibold text-gray-900">Dettagli Azienda</h3>
              </div>
              
              <div className="space-y-2 lg:space-y-4">
                <div>
                  <span className="text-sm font-medium text-gray-500">Nome Azienda</span>
                  <p className="text-lg font-semibold text-gray-900">{application.companyName}</p>
                </div>
                
                {application.companyDetails?.address && (
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-gray-500">Indirizzo</span>
                      <p className="text-gray-900">{application.companyDetails.address}</p>
                    </div>
                  </div>
                )}
                
                {application.companyDetails?.phone && (
                  <div className="flex items-start gap-3">
                    <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-gray-500">Telefono</span>
                      <p className="text-gray-900">{application.companyDetails.phone}</p>
                    </div>
                  </div>
                )}
                
                {application.companyDetails?.website && (
                  <div className="flex items-start gap-3">
                    <Globe className="w-5 h-5 text-gray-400 mt-0.5" />
                    <div>
                      <span className="text-sm font-medium text-gray-500">Sito Web</span>
                      <a 
                        href={application.companyDetails.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800"
                      >
                        {application.companyDetails.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sezione 3: Azioni */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6 h-full">
              <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-4">
                <div className="p-1.5 lg:p-2 bg-purple-100 rounded-lg lg:rounded-xl">
                  <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
                </div>
                <h3 className="text-base lg:text-xl font-semibold text-gray-900">Azioni</h3>
              </div>
              
              <div className="space-y-2 lg:space-y-3">
                <button
                  onClick={() => setShowStatusForm(true)}
                  className="w-full px-3 lg:px-4 py-2 lg:py-3 bg-blue-600 text-white rounded-lg lg:rounded-xl hover:bg-blue-700 transition-colors font-medium flex items-center justify-center gap-1.5 lg:gap-2 text-sm lg:text-base"
                >
                  <Edit className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  Aggiorna Stato
                </button>
                
                <button
                  onClick={() => navigate('/calendar', { state: { openAddModal: true } })}
                  className="w-full px-3 lg:px-4 py-2 lg:py-3 bg-green-600 text-white rounded-lg lg:rounded-xl hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-1.5 lg:gap-2 text-sm lg:text-base"
                >
                  <Plus className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  Aggiungi Colloquio
                </button>
                
                <button
                  onClick={() => {
                    if (window.confirm('Sei sicuro di voler eliminare questa candidatura?')) {
                      // Implementa eliminazione
                    }
                  }}
                  className="w-full px-3 lg:px-4 py-2 lg:py-3 bg-red-600 text-white rounded-lg lg:rounded-xl hover:bg-red-700 transition-colors font-medium flex items-center justify-center gap-1.5 lg:gap-2 text-sm lg:text-base"
                >
                  <Trash2 className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                  Elimina Candidatura
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sezione Allegati */}
        {application.attachments.length > 0 && (
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-2 lg:p-6">
            <div className="flex items-center gap-2 lg:gap-3 mb-2 lg:mb-4">
              <div className="p-1.5 lg:p-2 bg-orange-100 rounded-lg lg:rounded-xl">
                <Paperclip className="w-4 h-4 lg:w-5 lg:h-5 text-orange-600" />
              </div>
              <h3 className="text-base lg:text-xl font-semibold text-gray-900">
                Allegati ({application.attachments.length})
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 lg:gap-4">
              {application.attachments.map((attachment, index) => (
                <div key={index} className="flex items-center justify-between p-2 lg:p-4 bg-gray-50 rounded-lg lg:rounded-xl border">
                  <div className="flex items-center gap-2 lg:gap-3">
                    <Paperclip className="w-4 h-4 lg:w-5 lg:h-5 text-gray-400" />
                    <div>
                      <p className="font-medium text-gray-900 text-sm lg:text-base">{attachment.name}</p>
                      <p className="text-xs lg:text-sm text-gray-500">{attachment.type}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 lg:gap-2">
                    <a
                      href={attachment.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 lg:p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Visualizza"
                    >
                      <Eye className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </a>
                    <button
                      onClick={() => handleDeleteAttachment(index)}
                      className="p-1.5 lg:p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                      title="Elimina"
                    >
                      <Trash2 className="h-3.5 w-3.5 lg:h-4 lg:w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Modal Aggiorna Stato */}
      {showStatusForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-2 lg:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg">
            {/* Header */}
            <div className="p-4 lg:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Aggiorna Stato
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Modifica lo stato della candidatura
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowStatusForm(false);
                    setStatusNote('');
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-4 lg:p-6">
              <div className="space-y-4 lg:space-y-6">
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Nuovo Stato
                  </label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value as ApplicationStatus)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-all"
                  >
                    {statusOptions.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Note (opzionale)
                  </label>
                  <textarea
                    value={statusNote}
                    onChange={(e) => setStatusNote(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg transition-all resize-none"
                    placeholder="Aggiungi una nota per questo cambio di stato..."
                  />
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 lg:p-6 border-t border-gray-200">
              <div className="flex gap-2 lg:gap-3">
                <button
                  onClick={() => {
                    setShowStatusForm(false);
                    setStatusNote('');
                  }}
                  className="flex-1 px-4 lg:px-6 py-2 lg:py-3 border-2 border-gray-300 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm lg:text-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={handleStatusUpdate}
                  disabled={saving}
                  className="flex-1 px-4 lg:px-6 py-2 lg:py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg lg:rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm lg:text-lg shadow-lg"
                >
                  {saving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Aggiornando...
                    </span>
                  ) : (
                    'Aggiorna'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aggiungi Colloquio */}
      {showInterviewForm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50 p-2 lg:p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md sm:max-w-lg md:max-w-xl h-[90vh] sm:h-auto flex flex-col">
            {/* Header */}
            <div className="p-4 lg:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg lg:text-xl font-bold text-gray-900">
                    Aggiungi Colloquio
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    Programma un nuovo colloquio per questa candidatura
                  </p>
                </div>
                <button
                  onClick={() => setShowInterviewForm(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="w-4 h-4 lg:w-5 lg:h-5" />
                </button>
              </div>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 lg:p-6">
              <div className="space-y-4 lg:space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Data *
                    </label>
                    <input
                      type="date"
                      value={interviewDate}
                      onChange={(e) => setInterviewDate(e.target.value)}
                      className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                      required
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-700">
                      Ora *
                    </label>
                    <input
                      type="time"
                      value={interviewTime}
                      onChange={(e) => setInterviewTime(e.target.value)}
                      className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                      required
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Tipo Colloquio
                  </label>
                  <select
                    value={interviewType}
                    onChange={(e) => setInterviewType(e.target.value as 'Presenza' | 'Online' | 'Telefono')}
                    className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                  >
                    <option value="Presenza">Presenza</option>
                    <option value="Online">Online</option>
                    <option value="Telefono">Telefono</option>
                  </select>
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Luogo/Indirizzo
                  </label>
                  <input
                    type="text"
                    value={interviewLocation}
                    onChange={(e) => setInterviewLocation(e.target.value)}
                    className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                    placeholder="Indirizzo o link per il colloquio"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Contatto
                  </label>
                  <input
                    type="text"
                    value={interviewContact}
                    onChange={(e) => setInterviewContact(e.target.value)}
                    className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base"
                    placeholder="Nome del contatto"
                  />
                </div>
                
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-700">
                    Note
                  </label>
                  <textarea
                    value={interviewNotes}
                    onChange={(e) => setInterviewNotes(e.target.value)}
                    rows={3}
                    className="w-full px-3 lg:px-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm lg:text-base resize-none"
                    placeholder="Note aggiuntive per il colloquio..."
                  />
                </div>
                
                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="reminder"
                    checked={interviewReminder}
                    onChange={(e) => setInterviewReminder(e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="reminder" className="text-sm font-medium text-gray-700">
                    Attiva promemoria per questo colloquio
                  </label>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="p-4 lg:p-6 border-t border-gray-200">
              <div className="flex gap-2 lg:gap-3">
                <button
                  onClick={() => setShowInterviewForm(false)}
                  className="flex-1 px-4 lg:px-6 py-2 lg:py-3 border-2 border-gray-300 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-50 transition-all font-semibold text-sm lg:text-lg"
                >
                  Annulla
                </button>
                <button
                  onClick={handleAddInterview}
                  disabled={saving || !interviewDate || !interviewTime}
                  className="flex-1 px-4 lg:px-6 py-2 lg:py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg lg:rounded-xl hover:from-green-700 hover:to-green-800 transition-all disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm lg:text-lg shadow-lg"
                >
                  {saving ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Aggiungendo...
                    </span>
                  ) : (
                    'Aggiungi Colloquio'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 